import { ArrowLeft, Thermometer, Info, Watch } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const SkinTempDetail = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Skin Temperature
          </h1>
        </div>

        {/* Sleep Tracking Notice */}
        <Card className="border-warning/50 bg-warning/10 rounded-xl border-2 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-warning/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
              <Watch className="text-warning h-5 w-5" />
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">
                Sleep Tracking Required
              </h4>
              <p className="text-xs text-muted-foreground">
                Skin temperature variability is recorded only when you wear your
                tracker watch while sleeping. Make sure to wear it overnight for
                accurate readings.
              </p>
            </div>
          </div>
        </Card>

        {/* Current Skin Temp Variation */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-chart-3/10 flex h-16 w-16 items-center justify-center rounded-xl">
              <Thermometer className="text-chart-3 h-8 w-8" />
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">
                Variation from Baseline
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-foreground">-0.1</span>
                <span className="text-lg text-muted-foreground">°C</span>
              </div>
            </div>
          </div>
        </Card>

        {/* What is Skin Temperature Variability */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">
              What is Skin Temperature Variability?
            </h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">
                Skin temperature variability
              </strong>{" "}
              measures changes in your wrist skin temperature during sleep
              compared to your personal baseline. It's shown as a deviation (+
              or -) from your average.
            </p>
            <p>
              <strong className="text-foreground">
                Your body's temperature
              </strong>{" "}
              naturally fluctuates throughout the day and night. During sleep,
              your core temperature drops slightly, while skin temperature may
              increase as blood flow shifts to your extremities.
            </p>
            <p>
              <strong className="text-foreground">Significant changes</strong>{" "}
              from your baseline can indicate illness, hormonal changes (like
              ovulation), or environmental factors. Tracking these patterns can
              provide early warning signs of health changes.
            </p>
          </div>
        </Card>

        {/* What Can Affect Skin Temperature */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            Factors That Affect Skin Temperature
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-3">•</span>
              <span>Illness or fever developing</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3">•</span>
              <span>Menstrual cycle phases</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3">•</span>
              <span>Room temperature and bedding</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3">•</span>
              <span>Alcohol consumption</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3">•</span>
              <span>Exercise close to bedtime</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SkinTempDetail;
